/**
 * `loadGameBundle` — the whole airlock, assembled (architecture.md §6):
 * acquire → validate → cross-check → resolve base vocabulary → merge →
 * compile → index.
 *
 * @remarks
 * Result-typed end to end (D07 spirit): every way a bundle can fail to load
 * is a `LoadFailure` value in a `failed` outcome, never a thrown exception.
 * Failures COLLECT where stages are independent — semantic misses and a
 * lexicon that will not compile are all reported in one pass — but a stage
 * whose OUTPUT the next stage needs (acquisition, schema validation) stops
 * the walk, because there is nothing sound to continue with.
 *
 * Shape notes: R3 deliberately shipped no `narrativeLayer` placeholder, and
 * Phase 3 vindicated the refusal — the narrative layer arrived INSIDE the
 * pack (logs on their carrier items, voice lines / triggers / endings as
 * pack fields, R11–R12), so no separate bundle member ever existed to lie
 * about. The base vocabulary arrives through the caller's `baseVocabularies`
 * registry, keyed by the manifest's `extendsVocabulary` declaration — the
 * base is part of the bundle's declared identity, never ambient loader state
 * (replay determinism owns that call).
 *
 * @packageDocumentation
 */

import type { EngineGameBundle } from '@orpheus/engine-runtime';
import type { z } from 'zod';

import {
	CONTENT_SCHEMA_VERSION,
	type ContentPackDefinition,
	contentPackDefinitionSchema,
	type LexiconDefinition,
	type PackManifest,
} from '@orpheus/content-schema';
import { type CompiledLexicon, compileLexicon } from '@orpheus/parser-engine';

import type { LoadWarning } from './load-warning.js';

import { validateActorAuthoring, validateActorVerbKeys } from './actor-validation.js';
import { validateAudioAuthoring } from './audio-validation.js';
import { buildBundleIndexes } from './bundle-indexes.js';
import { validateCombatAuthoring } from './combat-validation.js';
import { validateConditionProducibility } from './condition-producibility.js';
import { findCountersPrintedBeforeMoved, validateCounterAuthoring } from './counter-validation.js';
import { describeSchemaIssue } from './describe-schema-issue.js';
import { desugarActorBlocks } from './desugar-actor-blocks.js';
import { desugarCheckpointRooms } from './desugar-checkpoint-rooms.js';
import { findOneWayDirections, validateDirectionOpposites } from './direction-opposites.js';
import { validateDurabilityAuthoring } from './durability-validation.js';
import { validateEquipmentAuthoring } from './equipment-validation.js';
import { findEventsWaitingOnThemselves } from './event-self-reference.js';
import { validateExitWords } from './exit-word-rules.js';
import { type GameBundleSource } from './game-bundle-source.js';
import { validateImageGeometry } from './geometry-validation.js';
import { validateWorldGraph } from './graph-validation.js';
import { validateGroupAuthoring } from './group-validation.js';
import { validateImageAuthoring } from './image-validation.js';
import { type LoadFailure } from './load-failure.js';
import { findUndrawableCorridors } from './map-kind-drawability.js';
import { findBaseAliasYields, findBaseEntryYields, findOverrideAliasDrops, mergeLexiconDefinitions } from './merge-lexicon-definitions.js';
import { validateNarrativeAuthoring } from './narrative-validation.js';
import { validateOccurrenceProducibility } from './occurrence-producibility.js';
import { validatePacingAuthoring } from './pacing-validation.js';
import { walkPackMigrations } from './pack-migrations.js';
import { findPaidRoomsOnClientGame, validatePaywallAuthoring } from './paywall-validation.js';
import { type ResolvedPresentation, resolvePresentation } from './presentation-defaults.js';
import { validatePromptAuthoring } from './prompt-validation.js';
import { validateRecordAuthoring } from './record-validation.js';
import {
	validateAuthoredVerbInteractions,
	validateAuthoredVerbNarratives,
	validateCarryDeclarations,
	validateContainment,
	validateContentPackSemantics,
} from './semantic-validation.js';
import { findSilentSpeakers, validateSpeakerAuthoring } from './speaker-validation.js';
import { validateSpriteAuthoring } from './sprite-validation.js';
import { validateTopicAuthoring } from './topic-validation.js';
import { validateUseArmsReachable } from './use-arm-validation.js';
import { findVariantsRepeatingFallback } from './variant-duplication.js';
import { findUnreachableFallbacks } from './variant-reachability.js';

/**
 * Everything a runtime consumes for one game — the airlock's finished
 * product. Extends the engine's bundle PORT (D20): `contentPack`, `indexes`,
 * and (since R6) `compiledLexicon` are the `EngineGameBundle` contract,
 * declared here so drift between what this loader produces and what the
 * engine consumes is a compile error at THIS site. The runtime manifest
 * rides alongside for application shells.
 */
export interface LoadedGameBundle extends EngineGameBundle {
	readonly runtimeManifest: RuntimeBundleManifest;
}

/** Caller-supplied load environment. */
export interface LoadGameBundleOptions {
	/**
	 * The base vocabularies this environment can resolve, keyed by the name a
	 * manifest's `extendsVocabulary` declares (D16). A pack declaring a base
	 * absent from this registry is a hard `reference_broken` failure.
	 */
	readonly baseVocabularies?: Readonly<Record<string, LexiconDefinition>>;
}

/** Bundle identity + provenance, denormalized so consumers never dig through the pack for it. */
export interface RuntimeBundleManifest {
	/** The base vocabulary that was actually merged, when the manifest declared one. */
	readonly appliedBaseVocabulary?: string;
	/**
	 * R24 (D93): where the pack's asset paths resolve, carried from the SOURCE
	 * — a plate's `src` is pack-relative, and only the source knows where the
	 * pack's files are served. Shells resolve against this without knowing
	 * which source kind produced the bundle; absent, `src` passes through
	 * verbatim (a pack with no assets, or a shell that serves them at root).
	 */
	readonly assetBaseUrl?: string;
	readonly entryRoomId: PackManifest['entryRoomId'];
	readonly gameId: PackManifest['gameId'];
	/**
	 * D132: what the shell shows around the game, RESOLVED — every label
	 * present, the assets present or honestly absent — so no shell carries a
	 * second copy of the defaults.
	 */
	readonly presentation: ResolvedPresentation;
	readonly schemaVersion: PackManifest['schemaVersion'];
	readonly seedPolicy: PackManifest['seedPolicy'];
	/** Which source produced the bundle document (audit/debug provenance). */
	readonly sourceDescription: string;
	readonly title: PackManifest['title'];
}

/**
 * How one load attempt ended. A loaded bundle carries its WARNINGS (D137):
 * things the author did that the loader cannot tell from accidents — never
 * blocking, always named, so a gate can print them and a studio can show
 * them.
 */
export type GameBundleLoadOutcome =
	| { readonly bundle: LoadedGameBundle; readonly type: 'loaded'; readonly warnings: readonly LoadWarning[] }
	| { readonly failures: readonly LoadFailure[]; readonly type: 'failed' };

/**
 * Load one game bundle from a source, end to end.
 *
 * @param source - Where the untrusted document comes from
 * @param options - The load environment (base-vocabulary registry)
 * @returns The loaded bundle, or every failure found
 */
export const loadGameBundle = async (
	source: GameBundleSource,
	options: LoadGameBundleOptions = {},
): Promise<GameBundleLoadOutcome> => {
	// 1. Acquire — the only I/O.
	let rawDocument: unknown;

	try {
		rawDocument = await source.acquire();
	} catch (acquireError) {
		return failed({
			message: acquireError instanceof Error ? acquireError.message : String(acquireError),
			sourceDescription: source.description,
			type: 'source_unreachable',
		});
	}

	// 1.5 — R13: walk the pack-migration registry BEFORE validation, so an
	// older authored document reaches the schema wearing the shape of now.
	// (The save opener learned this ordering the hard way in R11: validating
	// before migrating refuses everything the registry exists to rescue.)
	const migratedDocument = walkPackMigrations(rawDocument);

	// 2. Schema validation — nothing downstream is sound without it.
	const parseResult = contentPackDefinitionSchema.safeParse(migratedDocument);

	if (!parseResult.success) {
		return failed(...schemaFailures(parseResult.error.issues, migratedDocument));
	}

	// D142: `room.isCheckpoint` becomes its trigger here, before the
	// cross-checks, so the synthesized trigger is validated like an authored one.
	// D233: and `actor.blocksExits` becomes each exit's `blockedWhen` hold, for the same reason.
	const contentPack = desugarActorBlocks(desugarCheckpointRooms(parseResult.data));
	const failures: LoadFailure[] = [];

	// 3. Semantic cross-checks — all collected.
	failures.push(...validateContentPackSemantics(contentPack));
	// D245: a `use` arm an earlier unconditioned arm aimed the same way shadows could never run.
	failures.push(...validateUseArmsReachable(contentPack));
	// D50: containment is a pack-internal relationship, so it joins the step-3
	// cross-checks rather than waiting for the vocabulary compile.
	failures.push(...validateContainment(contentPack));
	// R25: bulk and capacity must be able to mean something — dead numbers are
	// the same defect class as dead beats (D47 family).
	failures.push(...validateCarryDeclarations(contentPack));
	// D61: the graph layer — the world must be walkable, not merely resolvable.
	failures.push(...validateWorldGraph(contentPack));
	// R12: the narrative layer — voice lines speakable, matchers hearable,
	// non-turn occurrences voice-only.
	failures.push(...validateNarrativeAuthoring(contentPack));
	// D143: the wear layer — slots declared and used, hooks and lines resolved.
	failures.push(...validateEquipmentAuthoring(contentPack));
	// D217: the group layer — ids unique, memberships declared, every group a set.
	failures.push(...validateGroupAuthoring(contentPack));
	// D152: the wear-out layer — every spending act one the thing can undergo.
	failures.push(...validateDurabilityAuthoring(contentPack));
	// D241: the Record — every flag it remembers and every ending that writes it is declared.
	failures.push(...validateRecordAuthoring(contentPack));
	// D231: the topic layer — ids unique, id keys resolved, fallbacks with a table to fall from.
	failures.push(...validateTopicAuthoring(contentPack));
	// D232: the actor layer — ids unique and one space with things, placed, voiced by one declared speaker each, prose walked.
	failures.push(...validateActorAuthoring(contentPack));
	// D234: the combat layer — a foe needs a body to strike and a body a foe, every death ending declared, every fight line walked.
	failures.push(...validateCombatAuthoring(contentPack));
	// D235: the prompt layer — ids unique, every prompt opened by some door, leadsTo its own actor's and never a dead loop, gifts things the hands can hold.
	failures.push(...validatePromptAuthoring(contentPack));
	// D244: the counters — unique, edges reachable and never chained, stocks able to give, prose printing declared amounts.
	failures.push(...validateCounterAuthoring(contentPack));
	// D153: the clock — conditions walked, the countdown's event really scheduled.
	failures.push(...validatePacingAuthoring(contentPack));
	// D165: the paywall — a wall needs words, words need a wall.
	failures.push(...validatePaywallAuthoring(contentPack));
	// D192: every voice a line speaks in is one the pack declared.
	failures.push(...validateSpeakerAuthoring(contentPack));
	// R23: the imagery layer — plates resolve both directions, and every
	// depicts declaration holds in every room that shows the plate (D89).
	failures.push(...validateAudioAuthoring(contentPack));
	failures.push(...validateImageAuthoring(contentPack));
	// R31: the plate-geometry checks — declarations against every naming room.
	failures.push(...validateImageGeometry(contentPack));
	// R32: the scene layer — sprites resolve both directions, promises hold
	// per binding room, and the silhouette rule's placement shadow is enforced.
	failures.push(...validateSpriteAuthoring(contentPack));
	// D184: every leaf condition asks for a fact the pack can make true — the
	// fifteen leaf types D56's constant-condition check never decided.
	failures.push(...validateConditionProducibility(contentPack));

	// 4. Base-vocabulary resolution (D16 / R3 ruling C) + merge + compile.
	const declaredBaseName = contentPack.manifest.extendsVocabulary;
	const resolvedBase = declaredBaseName === undefined ? undefined : options.baseVocabularies?.[declaredBaseName];

	if (declaredBaseName !== undefined && resolvedBase === undefined) {
		failures.push({
			message: `the manifest extends base vocabulary "${declaredBaseName}", but the load environment does not provide it`,
			referencePath: 'manifest.extendsVocabulary',
			targetId: declaredBaseName,
			type: 'reference_broken',
		});
	}

	let compiledLexicon: CompiledLexicon | undefined;
	const paidOnClient = findPaidRoomsOnClientGame(contentPack);
	const warnings: LoadWarning[] = [
		...findVariantsRepeatingFallback(contentPack).map((repeat): LoadWarning => ({ ...repeat, type: 'variant_repeats_fallback' })),
		// D159: an event waiting on itself is strange rather than wrong — reported, not refused.
		...findEventsWaitingOnThemselves(contentPack).map((waiting): LoadWarning => ({ ...waiting, type: 'event_waits_on_itself' })),
		// D165: a wall on a game that ships whole is a sign — reported, so the creator flips the game to `server` before selling it.
		...(paidOnClient === undefined ? [] : [{ ...paidOnClient, type: 'paid_rooms_on_client_game' as const }]),
		// D175: a fallback no variant can miss is three readings shipped as two — reported, not refused.
		...findUnreachableFallbacks(contentPack).map((unreachable): LoadWarning => ({ ...unreachable, type: 'fallback_unreachable' })),
		// D175: a corridor the hologram will draw as a room — the author hears it here, not from the map.
		...findUndrawableCorridors(contentPack).map((undrawable): LoadWarning => ({ ...undrawable, type: 'map_kind_undrawable' })),
		// D179: a manual-save choice a server game cannot honor — reported, so the promise comes off the manifest.
		...(contentPack.manifest.saves === 'manual' && contentPack.manifest.play === 'server' ? [{ type: 'manual_saves_on_server_game' as const }] : []),
		// D244: a line printing an amount its own arm then moves — reported, because the earlier amount is sometimes the point.
		...findCountersPrintedBeforeMoved(contentPack).map((printed): LoadWarning => ({ ...printed, type: 'counter_printed_before_move' })),
		// D192: a declared speaker nothing speaks in — reported, so the declaration earns a line or goes.
		...findSilentSpeakers(contentPack).map((silent): LoadWarning => ({ ...silent, type: 'speaker_silent' })),
	];

	if (declaredBaseName === undefined || resolvedBase !== undefined) {
		const mergedLexicon =
			resolvedBase === undefined ? contentPack.vocabulary : mergeLexiconDefinitions(resolvedBase, contentPack.vocabulary);

		if (resolvedBase !== undefined) {
			// D137: the merge's companion reads the same two layers and names
			// every base alias an override left behind — reported, not refused.
			warnings.push(
				...findOverrideAliasDrops(resolvedBase, contentPack.vocabulary).map(
					(drop): LoadWarning => ({ ...drop, type: 'override_dropped_aliases' }),
				),
				// D226: and every base word the pack took for one of its own entries.
				...findBaseAliasYields(resolvedBase, contentPack.vocabulary).map(
					(yielded): LoadWarning => ({ ...yielded, type: 'base_alias_yielded' }),
				),
				// D235: and every base ENTRY whose name the pack spells as a word of its own.
				...findBaseEntryYields(resolvedBase, contentPack.vocabulary).map(
					(yielded): LoadWarning => ({ ...yielded, type: 'base_entry_yielded' }),
				),
			);
		}

		const compilationOutcome = compileLexicon(mergedLexicon);

		if (compilationOutcome.type === 'invalid') {
			failures.push({
				issues: compilationOutcome.issues,
				message: 'the merged vocabulary was rejected by compileLexicon',
				type: 'lexicon_invalid',
			});
		} else {
			compiledLexicon = compilationOutcome.lexicon;
			// D47: the per-item verb answers can only be judged against the
			// MERGED vocabulary — `knock` reaches most packs from the base, not
			// from their own `vocabulary` — so this check waits for the compile
			// rather than joining the step-3 cross-checks.
			failures.push(...validateAuthoredVerbNarratives(contentPack, compilationOutcome.lexicon));
			// D232: an actor's refusals are keyed by the same light — canonical, declared, and never a verb it answers; D252: and its orders.
			failures.push(...validateActorVerbKeys(contentPack, compilationOutcome.lexicon));
			// D196: the table that ACTS is judged by the same light — and against the engine's own roster.
			failures.push(...validateAuthoredVerbInteractions(contentPack, compilationOutcome.lexicon));
			// D227: an exit's other words are directions, each answered by one exit of its room.
			failures.push(...validateExitWords(contentPack, compilationOutcome.lexicon));
			// D244: a declared opposite names a real direction that names it back — and the rooms are held to the pair.
			failures.push(...validateDirectionOpposites(mergedLexicon, compilationOutcome.lexicon));
			warnings.push(...findOneWayDirections(contentPack, mergedLexicon).map((oneWay): LoadWarning => ({ ...oneWay, type: 'direction_one_way' })));
		}
	}

	// R15: occurrence producibility — "no trigger listens for an occurrence
	// this pack cannot produce". Placed after the compile attempt because the
	// lexicon tier judges the MERGED vocabulary (the D47 pattern); the static
	// tier judges either way, and lexicon-tier rules abstain — never guess —
	// on a load with no compiled vocabulary to consult.
	failures.push(...validateOccurrenceProducibility(contentPack, compiledLexicon));

	if (compiledLexicon !== undefined && failures.length === 0) {
		// 5. Index + assemble — only a fully clean pack becomes a bundle.
		return {
			bundle: {
				compiledLexicon,
				contentPack,
				indexes: buildBundleIndexes(contentPack, resolvedBase),
				runtimeManifest: buildRuntimeManifest(contentPack, source, declaredBaseName),
			},
			type: 'loaded',
			warnings,
		};
	}

	return { failures, type: 'failed' };
};

/**
 * Partition raw schema issues into `version_unsupported` (issues on
 * `manifest.schemaVersion`, with the declared version recovered from the raw
 * document) and `schema_invalid` (everything else).
 *
 * @param issues - The Zod issues from the failed parse
 * @param rawDocument - The unparsed document (for recovering the declared version)
 * @returns One or two failures, version first
 */
const schemaFailures = (issues: readonly z.core.$ZodIssue[], rawDocument: unknown): LoadFailure[] => {
	const versionIssues = issues.filter(
		(issue) => issue.path[0] === 'manifest' && issue.path[1] === 'schemaVersion',
	);
	const otherIssues = issues.filter((issue) => !(issue.path[0] === 'manifest' && issue.path[1] === 'schemaVersion'));
	const failures: LoadFailure[] = [];
	// Destructured rather than length-tested: the first issue IS the emptiness
	// test, so there is no `?? fallback` on an index the check already proved
	// (engineering_standards §14.1 — restructure, never guard-and-ignore).
	const [firstVersionIssue] = versionIssues;
	// Recovered HERE rather than inside the version branch below, and for the
	// same reason: EVERY failing document reaches this line — a bare `42`, a
	// document with no `manifest` — whereas a Zod issue at
	// `manifest.schemaVersion` cannot exist unless the document is an object
	// carrying an object manifest. Read down there, the shape guards in
	// `declaredSchemaVersionOf` would be branches no input could enter.
	const declaredSchemaVersion = declaredSchemaVersionOf(rawDocument);

	if (firstVersionIssue !== undefined) {
		failures.push({
			message: firstVersionIssue.message,
			requestedVersion: declaredSchemaVersion,
			supportedVersions: [CONTENT_SCHEMA_VERSION],
			type: 'version_unsupported',
		});
	}
	if (otherIssues.length > 0) {
		failures.push({
			issues: otherIssues.map((issue) => describeSchemaIssue(issue)),
			message: 'the bundle document does not satisfy the content schemas',
			type: 'schema_invalid',
		});
	}

	return failures;
};

/**
 * Recover the schema version a raw document DECLARED, without trusting any
 * other part of its shape.
 *
 * @remarks
 * Called for every failed parse, so the shape guards below are live: the
 * document may be no object at all (`42`), an object with no `manifest`, or a
 * manifest with no readable `schemaVersion` — each of which is a real input
 * this loader's suite feeds it.
 *
 * @param rawDocument - The unparsed document, in whatever shape the source produced
 * @returns The declared version string, or `(undeclared)`
 */
const declaredSchemaVersionOf = (rawDocument: unknown): string => {
	if (typeof rawDocument === 'object' && rawDocument !== null && 'manifest' in rawDocument) {
		const manifestCandidate = (rawDocument as { readonly manifest: unknown }).manifest;

		if (
			typeof manifestCandidate === 'object' &&
			manifestCandidate !== null &&
			'schemaVersion' in manifestCandidate &&
			typeof (manifestCandidate as { readonly schemaVersion: unknown }).schemaVersion === 'string'
		) {
			return (manifestCandidate as { readonly schemaVersion: string }).schemaVersion;
		}
	}

	return '(undeclared)';
};

/**
 * Denormalize bundle identity + provenance.
 *
 * @param contentPack - The validated pack (its manifest, and its audio table for the theme)
 * @param source - The source that produced the document (description + asset base)
 * @param appliedBaseVocabulary - The base that was merged, if declared
 * @returns The runtime manifest
 */
const buildRuntimeManifest = (
	contentPack: ContentPackDefinition,
	source: GameBundleSource,
	appliedBaseVocabulary: string | undefined,
): RuntimeBundleManifest => ({
	...(appliedBaseVocabulary === undefined ? {} : { appliedBaseVocabulary }),
	...(source.assetBaseUrl === undefined ? {} : { assetBaseUrl: source.assetBaseUrl }),
	entryRoomId: contentPack.manifest.entryRoomId,
	gameId: contentPack.manifest.gameId,
	presentation: resolvePresentation(contentPack.manifest, contentPack.audio),
	schemaVersion: contentPack.manifest.schemaVersion,
	seedPolicy: contentPack.manifest.seedPolicy,
	sourceDescription: source.description,
	title: contentPack.manifest.title,
});

/**
 * Assemble a `failed` outcome.
 *
 * @param failures - The failures to carry
 * @returns The outcome
 */
const failed = (...failures: readonly LoadFailure[]): GameBundleLoadOutcome => ({ failures, type: 'failed' });
